import { ProductionRetentionContext, ProductionRetentionDecision } from './types';

/**
 * ESTRATÉGIA RETENÇÃO DE PRODUÇÃO
 * 
 * Conceito: Segurar pedidos SOLITÁRIOS (fora de clusters) que estão em produção
 * e têm bastante tempo de sobra, aguardando formar um "par logístico" com outro
 * pedido que venha da mesma região.
 * 
 * Diferença do Despacho Retido:
 * - Despacho Retido: Cluster já formado, só está aguardando cozinha
 * - Retenção de Produção: Pedido solitário, espera formar cluster para ganhar
 *   economia de escala (rota compartilhada)
 * 
 * Lógica de Negócio:
 * 1. Pedido está em produção (não está pronto)
 * 2. Pedido tem slack alto (folga de tempo)
 * 3. Estimamos que conseguiremos encontrar um "par" (outro pedido próximo)
 *    antes que o slack fique crítico
 * 4. Ao formar par, ganhamos economia na rota (dois pedidos, um entregador)
 * 
 * VIABILIDADE (critérios que devem ser atendidos):
 * 
 * C1 - Não está em drenagem:
 *    Se o hub está fechando, não retemos (despachamos tudo)
 * 
 * C2 - Slack suficiente para espera + execução:
 *    tempo_restante_vida > tempo_espera_par + tempo_rota_compartilhada + margem
 * 
 * C3 - Probabilidade de encontrar par é alta:
 *    Baseado em histórico de pedidos na região/horário
 *    Se a chance é baixa, não vale a pena reter
 * 
 * C4 - Economia esperada compensa o risco:
 *    Economia de rota compartilhada vs risco de terceirizar depois
 * 
 * @param context - Dados do pedido e métricas de contexto
 * @returns Decisão de retenção com probabilidade de sucesso
 */
export const evaluateProductionRetention = (
    context: ProductionRetentionContext
): ProductionRetentionDecision => {
    const order = context.order;
    
    // C1 - Verificação básica: não está pronto, não está em drenagem
    const c1_basicChecks = 
        !context.isDrainageActive &&
        context.elapsedMinutes <= context.maxHoldMinutes &&
        !order.isReady &&
        order.status !== 'ready' &&
        !order.ignoredStrategies?.includes('production_retention');

    if (!c1_basicChecks) {
        return { 
            hold: false,
            successProbability: 0
        };
    }

    // C2 - Slack deve ser suficiente para: espera + rota + margem
    // Usamos o remainingLifetimeMinutes do contexto (calculado no smartSplit)
    const remainingLifetime = context.remainingLifetimeMinutes;
    
    // Tempo estimado para encontrar um par logístico
    // Se não temos estimativa, usamos heurística: metade do tempo de cozinha
    const estimatedTimeToFindPair = context.estimatedTimeToFindPair ?? 10;
    
    // Tempo de rota compartilhada (estimativa conservadora)
    // Rota solitária: 1 pedido
    // Rota compartilhada: 2 pedidos (25% mais eficiente por pedido)
    const sharedRouteTime = 15; // minutos estimados para rota de 2 pedidos
    
    // Margem de segurança
    const safetyMargin = 5;
    
    // Tempo total necessário para retenção dar certo
    const totalRequiredTime = estimatedTimeToFindPair + sharedRouteTime + safetyMargin;
    
    const c2_hasEnoughTime = remainingLifetime >= totalRequiredTime;

    // C3 - Probabilidade de encontrar par
    // Heurística: baseada no tempo de vida restante vs tempo necessário
    // Se temos o dobro do tempo necessário, probabilidade é alta (80%)
    // Se temos apenas o suficiente, probabilidade é média (50%)
    let successProbability: number;
    if (remainingLifetime >= totalRequiredTime * 2) {
        successProbability = 0.8; // 80% - Folga confortável
    } else if (remainingLifetime >= totalRequiredTime * 1.3) {
        successProbability = 0.6; // 60% - Folga moderada
    } else if (remainingLifetime >= totalRequiredTime) {
        successProbability = 0.4; // 40% - Folga mínima, arriscado
    } else {
        successProbability = 0.1; // 10% - Provavelmente não dará tempo
    }

    const c3_highProbability = successProbability >= 0.5; // Só reter se >= 50% de chance

    // C4 - Threshold de slack deve ser respeitado
    // Este é o threshold configurado no sistema
    const c4_meetsThreshold = context.analysisSlackMinutes > context.retentionThreshold;

    // LOG DE DECISÃO
    const decisionLog = {
        c1_basicChecks,
        c2_hasEnoughTime,
        c3_highProbability,
        c4_meetsThreshold,
        remainingLifetime,
        estimatedTimeToFindPair,
        totalRequiredTime,
        successProbability,
        slackMinutes: context.analysisSlackMinutes,
        retentionThreshold: context.retentionThreshold
    };

    // Decide se retém baseado em todos os critérios
    const shouldHold = c2_hasEnoughTime && c3_highProbability && c4_meetsThreshold;

    if (!shouldHold) {
        return { 
            hold: false,
            successProbability,
            trace: undefined
        };
    }

    // ESTRATÉGIA VIÁVEL - Retém o pedido solitário
    return {
        hold: true,
        successProbability,
        trace: {
            kind: 'strategy',
            mode: 'hold',
            decisionReason: 'RETENTION_STRATEGY',
            orderIds: [order.id],
            chosenLabel: 'PRODUCTION_RETENTION',
            rationale: `Retenção de produção: Probabilidade ${(successProbability * 100).toFixed(0)}% de encontrar par ` +
                      `em ${estimatedTimeToFindPair}min. Vida útil: ${remainingLifetime.toFixed(1)}min. ` +
                      `Necessário: ${totalRequiredTime}min. ` +
                      `Economia esperada: rota compartilhada reduz custo por pedido.`,
            slackMin: context.analysisSlackMinutes,
            constraints: [
                'production_retention', 
                'kitchen_time', 
                'sla_single_zero_tolerance',
                `success_probability_${(successProbability * 100).toFixed(0)}pct`,
                `wait_time_${estimatedTimeToFindPair}min`
            ]
        }
    };
};
